import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysDataSubscription } from '../queryKeys';
import { type ExecutionLogFilters } from '../types';

import { fetchExecutionLog } from './fetchExecutionLog';
import { type ExecutionLogResponse } from './validations';

/**
 * Generates the query options for the execution log query, e.g. for loaders.
 *
 * @param filters - Status, trigger-source, contact-point, DLN and pagination filters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
// fallow-ignore-next-line unused-export -- queryOptions are exported for loader reuse per the tanstack-query-hooks skill
export const getExecutionLogQueryOptions = <TData = ExecutionLogResponse>({
  filters = {},
  options,
}: {
  filters?: ExecutionLogFilters;
  options?: Omit<
    UseQueryOptions<ExecutionLogResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    // Server-paginated table: keep the previous page's rows while the next page
    // loads, so the DataGrid's rowCount never collapses to 0 (which would reset
    // the controlled page) and the table doesn't flash empty on filter changes.
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchExecutionLog(filters, { signal }),
    queryKey: queryKeysDataSubscription.getExecutionLog(filters),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for the data subscription execution log.
 *
 * @param filters - Status, trigger-source, contact-point, DLN and pagination filters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription
 */
export const useQueryExecutionLog = <TData = ExecutionLogResponse>(
  filters: ExecutionLogFilters = {},
  options?: Omit<
    UseQueryOptions<ExecutionLogResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getExecutionLogQueryOptions<TData>({ filters, options }));
